/* Office Catch editorial enhancements. No API requests, inventory or global theme changes. */
(() => {
  'use strict';
  const one = (root, selector) => root.querySelector(selector);
  const all = (root, selector) => Array.from(root.querySelectorAll(selector));
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

  function appearance(root) {
    const control = one(root, '[data-ocg-theme]');
    if (!control) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    let mode = 'system';
    try { mode = localStorage.getItem('ocg-guide-appearance') || mode; } catch (_) { /* Storage is optional. */ }
    if (!['system', 'light', 'dark'].includes(mode)) mode = 'system';
    const apply = () => {
      root.dataset.ocgThemeMode = mode === 'system' ? (media.matches ? 'dark' : 'light') : mode;
      control.value = mode;
    };
    control.addEventListener('change', () => {
      mode = control.value;
      apply();
      try { localStorage.setItem('ocg-guide-appearance', mode); } catch (_) { /* No effect on navigation. */ }
    });
    const onChange = () => { if (root.isConnected && mode === 'system') apply(); };
    if (media.addEventListener) media.addEventListener('change', onChange);
    apply();
    one(root, '[data-ocg-theme-control]').hidden = false;
  }

  function hub(root) {
    const grid = one(root, '[data-ocg-grid]');
    const cards = all(grid, '[data-ocg-card]');
    const filterPanel = one(root, '[data-ocg-filters]');
    const input = one(root, 'input[data-ocg-search]');
    const intent = one(root, 'select[data-ocg-intent]');
    const topics = all(root, '[data-ocg-topic-button]');
    const counter = one(root, '[data-ocg-count]');
    const empty = one(root, '[data-ocg-empty]');
    const loadArea = one(root, '[data-ocg-load-area]');
    const load = one(root, '[data-ocg-load]');
    const shown = one(root, '[data-ocg-shown]');
    const pageSize = Number(root.dataset.ocgPageSize) || 12;
    let topic = 'all';
    let visibleLimit = pageSize;
    let debounce;
    const allowedTopics = topics.map(button => button.dataset.ocgTopicButton);
    const allowedIntents = all(intent, 'option').map(option => option.value);
    const records = cards.map(card => ({
      card,
      topic: card.dataset.ocgTopic,
      intent: card.dataset.ocgIntent,
      text: normalize(card.dataset.ocgSearch),
      date: Number(card.dataset.ocgDate) || 0
    })).sort((a, b) => b.date - a.date);
    records.forEach(record => grid.appendChild(record.card));

    function readState() {
      const params = new URL(window.location.href).searchParams;
      topic = allowedTopics.includes(params.get('topic')) ? params.get('topic') : 'all';
      intent.value = allowedIntents.includes(params.get('intent')) ? params.get('intent') : 'all';
      input.value = params.get('q') || '';
      visibleLimit = pageSize;
    }
    function saveState() {
      const url = new URL(window.location.href);
      [['topic', topic === 'all' ? '' : topic], ['intent', intent.value === 'all' ? '' : intent.value], ['q', input.value.trim()]].forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value); else url.searchParams.delete(key);
      });
      // Keep Shopify page, section_id, locale and theme-preview parameters intact.
      try { window.history.replaceState(null, '', url); } catch (_) { /* Filters work without History API. */ }
    }
    function render(save = true) {
      const words = normalize(input.value).split(' ').filter(Boolean);
      const typeMatches = record => intent.value === 'all' || record.intent === intent.value;
      const textMatches = record => words.every(word => record.text.includes(word));
      const possible = records.filter(record => typeMatches(record) && textMatches(record));
      const matches = possible.filter(record => topic === 'all' || record.topic === topic);
      const visible = new Set(matches.slice(0, visibleLimit).map(record => record.card));
      records.forEach(record => { record.card.hidden = !visible.has(record.card); });
      topics.forEach(button => {
        const value = button.dataset.ocgTopicButton;
        button.setAttribute('aria-pressed', String(value === topic));
        one(button, '[data-ocg-topic-count]').textContent = String(value === 'all' ? possible.length : possible.filter(record => record.topic === value).length);
      });
      const count = matches.length;
      counter.textContent = `${count} ${count === 1 ? 'guide' : 'guides'}${topic === 'all' ? '' : ` in ${topic}`}${input.value.trim() ? ` matching “${input.value.trim()}”` : ''}`;
      empty.hidden = count !== 0;
      loadArea.hidden = count === 0;
      load.hidden = visibleLimit >= count;
      shown.textContent = `Showing ${Math.min(visibleLimit, count)} of ${count} guides on this page`;
      if (save) saveState();
      return matches;
    }
    function reset() {
      clearTimeout(debounce);
      input.value = '';
      intent.value = 'all';
      topic = 'all';
      visibleLimit = pageSize;
      render();
      input.focus({ preventScroll: true });
    }
    one(root, '.ocg-filter-form').addEventListener('submit', event => {
      event.preventDefault();
      clearTimeout(debounce);
      visibleLimit = pageSize;
      render();
    });
    input.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => { visibleLimit = pageSize; render(); }, 150);
    });
    intent.addEventListener('change', () => { visibleLimit = pageSize; render(); });
    topics.forEach(button => button.addEventListener('click', () => {
      topic = button.dataset.ocgTopicButton;
      visibleLimit = pageSize;
      render();
    }));
    all(root, '[data-ocg-reset]').forEach(button => button.addEventListener('click', reset));
    load.addEventListener('click', () => {
      const previousLimit = visibleLimit;
      visibleLimit += pageSize;
      const matches = render(false);
      const next = matches[previousLimit];
      if (next) one(next.card, 'a').focus({ preventScroll: true });
    });
    window.addEventListener('popstate', () => {
      if (!root.isConnected) return;
      readState();
      render(false);
    });
    readState();
    render(false);
    filterPanel.hidden = false;
    root.dataset.ocgEnhanced = 'true';
  }

  function article(root) {
    const prose = one(root, '[data-ocg-prose]');
    if (!prose) return;
    // DOM-only heading normalization: retain every attribute, anchor and child.
    // Never rewrite the stored HTML, schema, script strings or literal code examples.
    all(prose, 'h1').forEach(heading => {
      if (heading.closest('pre,code,template,svg')) return;
      const replacement = document.createElement('h2');
      Array.from(heading.attributes).forEach(attr => replacement.setAttribute(attr.name, attr.value));
      while (heading.firstChild) replacement.appendChild(heading.firstChild);
      heading.replaceWith(replacement);
    });
    // Highlight an actual explicit answer in place. Never synthesize or duplicate it.
    const answer = all(prose, 'p').find(p => /^quick\s+answer\s*[:—–-]/i.test((p.textContent || '').trim()));
    if (answer) answer.classList.add('ocg-quick-answer');
    const explicitHeading = all(prose, 'h2,h3').find(h => /^quick\s+answer\s*[:?]?$/i.test((h.textContent || '').trim()));
    if (explicitHeading && explicitHeading.nextElementSibling?.tagName === 'P') explicitHeading.nextElementSibling.classList.add('ocg-quick-answer');

    // Covers table variants and app-added tables. The native Liquid wrappers
    // already provide scrolling/labels without JavaScript.
    all(prose, 'table').forEach((table, index) => {
      let wrap = table.parentElement;
      if (!wrap.classList.contains('ocg-table-wrap')) {
        wrap = document.createElement('div');
        wrap.className = 'ocg-table-wrap';
        table.before(wrap);
        wrap.appendChild(table);
      }
      wrap.setAttribute('role', 'region');
      wrap.setAttribute('tabindex', '0');
      const caption = table.caption?.textContent.trim();
      wrap.setAttribute('aria-label', caption ? `${caption}. Scroll horizontally for all columns.` : `Article table ${index + 1}. Scroll horizontally for all columns.`);
    });
    const headings = all(prose, 'h2').filter(heading => heading.textContent.trim() && !heading.closest('pre,code,template'));
    const usedIds = new Set(all(document, '[id]').map(element => element.id));
    headings.forEach((heading, index) => {
      if (!heading.id) {
        let id = `ocg-section-${normalize(heading.textContent).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 65) || index + 1}`;
        const base = id;
        let suffix = 2;
        while (usedIds.has(id)) id = `${base}-${suffix++}`;
        heading.id = id;
        usedIds.add(id);
      }
    });
    if (headings.length) {
      all(root, '[data-ocg-toc-links]').forEach(nav => {
        const fragment = document.createDocumentFragment();
        headings.forEach(heading => {
          const link = document.createElement('a');
          link.href = `#${encodeURIComponent(heading.id)}`;
          link.textContent = heading.textContent.trim();
          link.addEventListener('click', () => {
            // Retain the native hash-link action; only improve keyboard focus.
            if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
          });
          fragment.appendChild(link);
        });
        nav.replaceChildren(fragment);
      });
      one(root, '[data-ocg-toc-desktop]').hidden = false;
      one(root, '[data-ocg-toc-mobile]').hidden = false;
    }
    const progress = one(root, '[data-ocg-progress]');
    const progressBar = one(progress, 'span');
    const links = all(root, '[data-ocg-toc-links] a');
    let ticking = false;
    function update() {
      ticking = false;
      if (!root.isConnected) return;
      const box = prose.getBoundingClientRect();
      const top = parseFloat(getComputedStyle(one(root, '.ocg-toc')).top) || 120;
      const span = Math.max(1, box.height - Math.max(0, window.innerHeight - top));
      const ratio = Math.max(0, Math.min(1, (top - box.top) / span));
      progressBar.style.transform = `scaleX(${ratio})`;
      let current = null;
      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= top + 40) current = heading.id;
      }
      links.forEach(link => {
        if (decodeURIComponent(link.hash.slice(1)) === current) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }
    const schedule = () => { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    all(prose, 'img').forEach(image => image.addEventListener('load', schedule, { once: true }));
    progress.hidden = false;
    update();
  }

  function init(scope = document) {
    const roots = all(scope, '[data-ocg-root]');
    if (scope.matches?.('[data-ocg-root]')) roots.unshift(scope);
    roots.forEach(root => {
      if (root.dataset.ocgInitialized) return;
      root.dataset.ocgInitialized = 'true';
      try { appearance(root); } catch (_) { /* Keep default light content when unsupported. */ }
      try {
        if (root.hasAttribute('data-ocg-hub')) hub(root);
        if (root.hasAttribute('data-ocg-article')) article(root);
      } catch (error) {
        // A failed enhancement must never make the native library inaccessible.
        all(root, '[data-ocg-card]').forEach(card => { card.hidden = false; });
        const panel = one(root, '[data-ocg-filters]');
        if (panel) panel.hidden = true;
        const loadArea = one(root, '[data-ocg-load-area]');
        if (loadArea) loadArea.hidden = true;
        const empty = one(root, '[data-ocg-empty]');
        if (empty) empty.hidden = true;
        if (window.Shopify?.designMode) console.warn('Office Catch guide enhancement unavailable:', error);
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
